import {PlantType} from "../../model/PlantType";
import * as rt from "@tanstack/react-table";
import * as bp from "@blueprintjs/core";
import React, {useCallback, useState} from "react";
import {AppTable, RowActions, SlicedCell} from "../../components/AppTable";
import {ImageButton, ImageManager, ImageManagerProps} from "../../components/ImageManager";
import {PlantTypesDialog, PlantTypesDialogProps} from "./PlantTpesDialog";
import PlantTypeService from "../../service/PlantTypeService";

export default function PlantTypesTable({plantTypes: data, refreshData: refreshPlantTypes}: {
    plantTypes: PlantType[],
    refreshData: () => Promise<void>
}) {
    const [dialogProps, setDialogProps] = useState<PlantTypesDialogProps>({
        isOpen: false,
        close: async () => {
            setDialogProps({...dialogProps, isOpen: false});
            await refreshPlantTypes();
        },
        mode: 'new',
        plantType: {
            id: '',
            name: '',
            scientificName: '',
            description: '',
            archived: false,
            previewImages: [],
            findings: 0
        }
    });
    const openCreateDialog = useCallback(() => setDialogProps((prev) => ({
        ...prev,
        isOpen: true,
        mode: 'new',
        plantType: {
            id: '',
            name: '',
            scientificName: '',
            description: '',
            archived: false,
            previewImages: [],
            findings: 0
        }
    })), []);
    const openEditDialog = useCallback((plantType: PlantType) => setDialogProps((prev) => ({
        ...prev,
        isOpen: true,
        mode: 'edit',
        plantType
    })), []);
    const [imageManagerProps, setImageManagerProps] = useState<ImageManagerProps>({
        isOpen: false,
        onClose: async () => {
            setImageManagerProps({...imageManagerProps, isOpen: false});
            await refreshPlantTypes();
        },
        title: '',
        loadUrls: async () => [],
        addImage: async (_)  => {},
        deleteImage: async (_) => {}
    });
    const columnHelper = rt.createColumnHelper<PlantType>();
    const handleDeletePlantType = useCallback(async (plantType: PlantType) => {
        await PlantTypeService.deletePlantType(plantType);
        await refreshPlantTypes();
    }, [refreshPlantTypes]);

    const openImageManagerForPlantType = useCallback((plantType: PlantType) => {
        setImageManagerProps((prev) => ({
            ...prev,
            isOpen: true,
            loadUrls: async () => (await PlantTypeService.getPlantType(plantType.id)).previewImages,
            addImage: async (file) => await PlantTypeService.uploadImage(plantType, file),
            deleteImage: async (url) => await PlantTypeService.removeImage(plantType, url.substring(url.lastIndexOf('/') + 1))
        }));
    }, []);

    const columns = React.useMemo(() => [
        columnHelper.display({
            id: 'select',
            header: ({table}) => (
                <bp.Checkbox
                    checked={table.getIsAllRowsSelected()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
            ),
            cell: ({row}) => (
                <bp.Checkbox
                    checked={row.getIsSelected()}
                    indeterminate={row.getIsSomeSelected?.()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
            enableSorting: false,
            enableColumnFilter: false
        }),
        columnHelper.accessor('archived', {
            cell: info => <bp.Checkbox checked={!info.getValue()} disabled/>,
            header: () => <span>Aktiv</span>,
            enableSorting: false,
            enableColumnFilter: false
        }),
        columnHelper.accessor('name', {
            cell: info => info.getValue(),
            header: () => <span>Name</span>,
        }),
        columnHelper.accessor('scientificName', {
            cell: info => info.getValue(),
            header: () => <span>Wissenschaftlicher Name</span>
        }),
        columnHelper.accessor('description', {
            cell: info => <SlicedCell text={info.getValue()} max={100}/>,
            header: () => <span>Beschreibung</span>,
            enableSorting: false,
            enableColumnFilter: false
        }),
        columnHelper.accessor('previewImages', {
            cell: info =>
                <ImageButton imageUrls={info.getValue()}
                             openDialog={() => openImageManagerForPlantType(info.row.original)}/>,
            header: () => <span>Bilder</span>,
            enableSorting: false,
            enableColumnFilter: false
        }),
        columnHelper.display({
            id: 'actions',
            cell: info =>
                <RowActions edit={() => openEditDialog(info.row.original)}
                            delete={() => handleDeletePlantType(info.row.original)}/>,
            header: () => <span>Aktionen</span>
        })
    ], [openEditDialog, handleDeletePlantType, openImageManagerForPlantType]);
    const [sorting, setSorting] = useState<rt.SortingState>([{id: 'name', desc: false}]);
    const [columnFilters, setColumnFilters] = useState<rt.ColumnFiltersState>([]);
    const [rowSelection, setRowSelection] = useState<rt.RowSelectionState>({});
    const table = rt.useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
            rowSelection
        },
        getCoreRowModel: rt.getCoreRowModel(),
        getSortedRowModel: rt.getSortedRowModel(),
        getFilteredRowModel: rt.getFilteredRowModel(),
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onRowSelectionChange: setRowSelection,
        enableRowSelection: true,
        getRowId: (row) => row.id
    });
    const selectedRows = table.getSelectedRowModel().flatRows;
    const selectedCount = selectedRows.length;
    const handleBulk = async (archived: boolean) => {
        if (selectedCount === 0) return;
        const actionText = archived ? 'archivieren' : 'entarchivieren';
        if (!window.confirm(`Möchten Sie ${selectedCount} Einträge ${actionText}?`)) return;
        await Promise.all(selectedRows.map(r => PlantTypeService.updatePlantType(r.original.id, {
            name: undefined,
            scientificName: undefined,
            description: undefined,
            archived,
        })));
        setRowSelection({});
        await refreshPlantTypes();
    };
    return <>
        <ImageManager {...imageManagerProps} />
        <PlantTypesDialog {...dialogProps} />
        <AppTable table={table}
                  create={openCreateDialog}
                  selectedCount={selectedCount}
                  onBulkArchive={async () => handleBulk(true)}
                  onBulkUnarchive={async () => handleBulk(false)}
        />
    </>;
}